import {
  afterNextRender,
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  inject,
  Injector,
  OnInit,
  signal,
} from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { map } from 'rxjs';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { KiProjekt, KiProjektStand } from '../../../models/ki-projekt.model';
import { KiProjekteService } from '../../../services/ki-projekte.service';
import { NotificationService } from '../../../services/notification.service';
import { SectionTitleComponent } from '../../../components/section-title/section-title.component';

const TOKENS_PRO_MIO = 1_000_000;

@Component({
  selector: 'app-ki-projektuebersicht',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [MatProgressSpinnerModule, TranslatePipe, SectionTitleComponent],
  templateUrl: './ki-projektuebersicht.component.html',
  styleUrl: './ki-projektuebersicht.component.scss',
})
export class KiProjektuebersichtComponent implements OnInit {
  private readonly service = inject(KiProjekteService);
  private readonly translate = inject(TranslateService);
  private readonly notification = inject(NotificationService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly injector = inject(Injector);

  private readonly currentLang = toSignal(this.translate.onLangChange.pipe(map((event) => event.lang)), {
    initialValue: this.translate.currentLang,
  });

  readonly projekte = signal<KiProjekt[]>([]);
  readonly loading = signal(true);
  readonly loadError = signal(false);
  readonly pending = signal<ReadonlySet<string>>(new Set());

  ngOnInit(): void {
    this.service
      .getProjekte()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (projekte) => {
          this.projekte.set(projekte);
          this.loading.set(false);
        },
        error: () => {
          this.loadError.set(true);
          this.loading.set(false);
        },
      });
  }

  istFreigegeben(projekt: KiProjekt): boolean {
    return projekt.stand !== 'GESPERRT';
  }

  istPending(projekt: KiProjekt): boolean {
    return this.pending().has(projekt.projectId);
  }

  umschalten(projekt: KiProjekt, schalter: HTMLButtonElement): void {
    if (this.istPending(projekt)) return;
    const hatteFokus = document.activeElement === schalter;
    const { projectId, stand: vorher } = projekt;
    const gesperrt = this.istFreigegeben(projekt);
    this.setStand(projectId, gesperrt ? 'GESPERRT' : 'AUS');
    this.setPending(projectId, true);
    this.service
      .setSperre(projectId, gesperrt)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        complete: () => {
          this.setPending(projectId, false);
          this.fokusZurueckgeben(schalter, hatteFokus);
        },
        error: () => {
          this.setStand(projectId, vorher);
          this.setPending(projectId, false);
          this.fokusZurueckgeben(schalter, hatteFokus);
          this.notification.error('kiProjektuebersicht.sperreFehler');
        },
      });
  }

  verbrauch(tokens: number): string {
    if (tokens <= 0) return '—';
    const wert = new Intl.NumberFormat(this.currentLang() || 'de', {
      minimumFractionDigits: 1,
      maximumFractionDigits: 1,
    }).format(tokens / TOKENS_PRO_MIO);
    return this.translate.instant('kiProjektuebersicht.verbrauchMio', { wert });
  }

  // Manche Browser nehmen dem Schalter den Fokus, solange er deaktiviert ist (NEX-1576 AK1). Zurückgegeben wird er
  // erst nach dem Rendern, wenn der Knopf wieder aktiv ist, und nur, wenn der Nutzer nicht woanders hin gewechselt ist.
  private fokusZurueckgeben(schalter: HTMLButtonElement, hatteFokus: boolean): void {
    if (!hatteFokus) return;
    afterNextRender(
      () => {
        const aktiv = document.activeElement;
        if (aktiv === null || aktiv === document.body) schalter.focus();
      },
      { injector: this.injector },
    );
  }

  private setPending(projectId: string, aktiv: boolean): void {
    this.pending.update((ids) => {
      const next = new Set(ids);
      if (aktiv) next.add(projectId);
      else next.delete(projectId);
      return next;
    });
  }

  private setStand(projectId: string, stand: KiProjektStand): void {
    this.projekte.update((liste) => liste.map((p) => (p.projectId === projectId ? { ...p, stand } : p)));
  }
}
